import { Command } from 'cmdk';
import { ArrowRight, Radio } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusDot } from '@/components/common/status-dot';
import { useSessions } from '@/hooks/use-sessions';
import { useI18n } from '@/i18n';
import { useAccess } from '@/hooks/use-access';
import { NAV } from './nav-items';

/**
 * ⌘K palette. It indexes navigation and live sessions - enough to get anywhere
 * in two keystrokes without becoming a second, worse search screen.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const { can } = useAccess();
  const { data: sessions } = useSessions();
  const { t } = useI18n();

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onOpenChange]);

  const go = (path: string) => {
    onOpenChange(false);
    navigate(path);
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label={t('palette.label')}
      className="fixed left-1/2 top-[18vh] z-50 w-[min(560px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-lg border border-line bg-surface shadow-pop animate-slide-up-fade"
    >
      <Command.Input
        placeholder={t('palette.placeholder')}
        className="h-11 w-full border-b border-line bg-transparent px-4 text-base outline-none placeholder:text-ink-subtle"
      />

      <Command.List className="max-h-[min(420px,60vh)] overflow-y-auto p-1.5">
        <Command.Empty className="px-3 py-8 text-center text-sm text-ink-muted">
          {t('palette.empty')}
        </Command.Empty>

        {sessions && sessions.length > 0 && (
          <Command.Group
            heading={t('palette.sessions')}
            className="[&_[cmdk-group-heading]]:label-caps [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
          >
            {sessions.map((session) => (
              <Command.Item
                key={session.id}
                value={`${session.name} ${session.profileName ?? ''} ${session.id}`}
                onSelect={() => go(`/sessions/${session.id}`)}
                className="flex h-8 cursor-pointer items-center gap-2 rounded px-2 text-sm data-[selected=true]:bg-surface-sunken"
              >
                <StatusDot status={session.status} />
                <span className="truncate">{session.name}</span>
                {session.profileName && (
                  <span className="truncate text-ink-subtle">{session.profileName}</span>
                )}
                <ArrowRight className="ml-auto h-3 w-3 text-ink-subtle opacity-0 data-[selected=true]:opacity-100" />
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {NAV.map((group) => {
          // Same gate as the sidebar: what is hidden there must not be
          // reachable from here either.
          const items = group.items.filter((item) => can(item.permission));
          if (!items.length) return null;

          return (
            <Command.Group
              key={group.labelKey}
              heading={t(group.labelKey)}
              className="[&_[cmdk-group-heading]]:label-caps [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
            >
              {items.map((item) => (
                <Command.Item
                  key={item.to}
                  value={`${t(group.labelKey)} ${t(item.labelKey)}`}
                  onSelect={() => go(item.to)}
                  className="flex h-8 cursor-pointer items-center gap-2 rounded px-2 text-sm data-[selected=true]:bg-surface-sunken"
                >
                  <item.icon className="h-3.5 w-3.5 text-ink-subtle" strokeWidth={1.75} />
                  {t(item.labelKey)}
                  {item.shortcut && <span className="kbd ml-auto">{item.shortcut}</span>}
                </Command.Item>
              ))}
            </Command.Group>
          );
        })}

        <Command.Group
          heading={t('palette.actions')}
          className="[&_[cmdk-group-heading]]:label-caps [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
        >
          <Command.Item
            value="new session create connect line account"
            onSelect={() => go('/sessions?new=1')}
            className="flex h-8 cursor-pointer items-center gap-2 rounded px-2 text-sm data-[selected=true]:bg-surface-sunken"
          >
            <Radio className="h-3.5 w-3.5 text-ink-subtle" strokeWidth={1.75} />
            {t('palette.newSession')}
          </Command.Item>
        </Command.Group>
      </Command.List>
    </Command.Dialog>
  );
}
